/**
 * ISO 4217 currency exponents (number of fraction digits of the minor unit).
 *
 * Only currencies whose exponent differs from the default of 2 are listed;
 * `getCurrencyExponent` falls back to 2 for anything unknown, which is the
 * safe assumption for new/rare codes and keeps the map small.
 */

export type CurrencyCode = string;

export const DEFAULT_CURRENCY_EXPONENT = 2;

/** Currencies with no minor unit (exponent 0). */
const ZERO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  "BIF", // Burundian franc
  "CLP", // Chilean peso
  "DJF", // Djiboutian franc
  "GNF", // Guinean franc
  "ISK", // Icelandic króna
  "JPY", // Japanese yen
  "KMF", // Comorian franc
  "KRW", // South Korean won
  "PYG", // Paraguayan guaraní
  "RWF", // Rwandan franc
  "UGX", // Ugandan shilling
  "UYI", // Uruguay peso en unidades indexadas
  "VND", // Vietnamese dong
  "VUV", // Vanuatu vatu
  "XAF", // Central African CFA franc
  "XOF", // West African CFA franc
  "XPF", // CFP franc
]);

/** Currencies with a thousandth minor unit (exponent 3). */
const THREE_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  "BHD", // Bahraini dinar
  "IQD", // Iraqi dinar
  "JOD", // Jordanian dinar
  "KWD", // Kuwaiti dinar
  "LYD", // Libyan dinar
  "OMR", // Omani rial
  "TND", // Tunisian dinar
]);

/** Fund/indexed units with exponent 4. */
const FOUR_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  "CLF", // Chilean unidad de fomento
  "UYW", // Uruguayan unidad previsional
]);

/**
 * Fraction digits for an ISO 4217 code. Unknown or malformed codes return the
 * default of 2 — never throws, so it is safe on untrusted wire data.
 */
export function getCurrencyExponent(code: CurrencyCode): number {
  const normalized = code.toUpperCase();
  if (ZERO_DECIMAL_CURRENCIES.has(normalized)) return 0;
  if (THREE_DECIMAL_CURRENCIES.has(normalized)) return 3;
  if (FOUR_DECIMAL_CURRENCIES.has(normalized)) return 4;
  return DEFAULT_CURRENCY_EXPONENT;
}
